import React, { useState, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useLoginTestSessionMutation } from "../app/services/meseeknaApiV2"
import LogoFull from "../components/logo-full"

const TestSessionLogin: React.FC = () => {
  const navigate = useNavigate()
  const { testSessionId } = useParams<{ testSessionId: string }>()

  // Form state
  const [email, setEmail] = useState("")
  const [passcode, setPasscode] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // API mutations
  const [loginTestSession] = useLoginTestSessionMutation()

  // Email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email.trim())
  }

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {}

    if (!email.trim()) {
      newErrors.email = "Please enter your email address"
    } else if (!isValidEmail(email)) {
      newErrors.email = "Please enter a valid email address"
    }

    if (!passcode.trim()) {
      newErrors.passcode = "Please enter your passcode"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [email, passcode])

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      const loginData = {
        email: email.trim(),
        passcode: passcode.trim().toUpperCase(),
        testSessionId: testSessionId!
      }

      const result = await loginTestSession(loginData).unwrap()

      // Store authentication data for the test session
      if (result.token) {
        localStorage.setItem("testSessionToken", result.token)
        localStorage.setItem("testSessionId", testSessionId!)
        localStorage.setItem("testSessionSimulationType", result.simulationType)

        // Store participant data
        if (result.userData?.participantId) {
          localStorage.setItem("testSessionParticipantId", result.userData.participantId)
        }
        if (result.userData?.id) {
          localStorage.setItem("testSessionUserId", result.userData.id.toString())
        }

        // Store user name data
        if (result.userData?.firstName) {
          localStorage.setItem("testSessionFirstName", result.userData.firstName)
        }
        if (result.userData?.lastName) {
          localStorage.setItem("testSessionLastName", result.userData.lastName)
        }

        // Store simulation data for the simulation page
        localStorage.setItem("testSessionSimulationData", JSON.stringify(result.simulationData))
      }

      // Navigate to the test simulation page
      navigate(`/test-sessions/${testSessionId}/simulation`)

    } catch (error: unknown) {
      console.error("Login failed:", error)
      const errorMessage = (error as { data?: { message?: string } })?.data?.message || "Invalid email or passcode. Please try again."
      setErrors({
        submit: errorMessage
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [validateForm, email, passcode, testSessionId, loginTestSession, navigate])

  // Handle input changes and clear errors
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: "" }))
    }
  }, [errors.email])

  const handlePasscodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPasscode(e.target.value.toUpperCase())
    if (errors.passcode) {
      setErrors(prev => ({ ...prev, passcode: "" }))
    }
  }, [errors.passcode])

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-col pt-[100px] px-8 pb-12 box-border items-center justify-start font-text-md-regular">

      {/* Logo */}
      <div className="mb-8">
        <LogoFull />
      </div>

      {/* Main Content */}
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8">

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Test Session Login</h1>
            <p className="text-gray-600 text-sm">
              Enter your email and the passcode to access this test simulation.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="your.email@company.com"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="text-red-600 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Passcode Field */}
            <div>
              <label htmlFor="passcode" className="block text-sm font-medium text-gray-700 mb-1">
                Passcode
              </label>
              <input
                type="text"
                id="passcode"
                value={passcode}
                onChange={handlePasscodeChange}
                placeholder="Enter your 6-character passcode"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono tracking-widest ${
                  errors.passcode ? "border-red-500" : "border-gray-300"
                }`}
                maxLength={6}
                disabled={isSubmitting}
              />
              {errors.passcode && (
                <p className="text-red-600 text-sm mt-1">{errors.passcode}</p>
              )}
            </div>

            {/* Submit Errors */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Signing In..." : "Start Test"}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              This is a test session for QA purposes. Your test runs will be saved for review.
            </p>
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Having trouble? Contact{" "}
            <a href="mailto:support@meseekna.com" className="text-blue-600 hover:text-blue-800">
              support@meseekna.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default TestSessionLogin
